import clsx from "clsx"

import type { useSliderState } from "@unlikelystudio/react-slider"

import type { InferReturn, PropsWithClassName } from "~/@types/generics"
import useGetSliderNavigationVisibility from "~/hooks/useGetSliderNavigationVisibility"
import ArrowButton from "~/components/ui/SliderNavigation/components/ArrowButton"
import { ChevronButton } from "~/components/ui/SliderNavigation/components/ChevronButton"
import type { SliderButtonTypes, SliderNavigationButtonProps } from "~/components/ui/SliderNavigation/types/button"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"

import * as css from "./styles.css"

type SliderNavigationProps = PropsWithClassName<
  InferReturn<typeof useSliderState>[0] & {
    buttonClassName?: string
    infinite?: boolean
    type?: keyof typeof css.layout
    unavailableState?: Exclude<keyof typeof css.state, "default">
    buttonType?: SliderButtonTypes
  }
>

function getButtonFromType(type: SliderNavigationProps["buttonType"], props: SliderNavigationButtonProps) {
  switch (type) {
    case "chevron":
      return <ChevronButton {...props} />
    case "arrow":
      return <ArrowButton {...props} />
  }
}

export function SliderNavigation({
  className,
  type = "cover",
  unavailableState = "hidden",
  buttonType = "chevron",
  buttonClassName,
  nextSlide,
  slideIndex,
  enableDrag,
  prevSlide,
  infinite,
  maxSlideIndex,
}: SliderNavigationProps) {
  const { isPrevVisible, isNextVisible } = useGetSliderNavigationVisibility({ slideIndex, enableDrag, maxSlideIndex })
  const t = useTranslate()

  if (!isNextVisible && !isPrevVisible && !infinite) return null
  const nextButtonProps = {
    className: clsx(css.state[isNextVisible || infinite ? "default" : unavailableState], buttonClassName),
    onClick: nextSlide,
    ariaLabel: t("aria_arrow_next"),
    direction: "right",
  } satisfies SliderNavigationButtonProps

  const prevButtonProps = {
    className: clsx(css.state[isPrevVisible || infinite ? "default" : unavailableState], buttonClassName),
    onClick: prevSlide,
    ariaLabel: t("aria_arrow_prev"),
  } satisfies SliderNavigationButtonProps

  const NextButton = getButtonFromType(buttonType, nextButtonProps)
  const PrevButton = getButtonFromType(buttonType, prevButtonProps)

  return (
    <div className={clsx(css.SliderNavigation, css.layout[type], className)}>
      {PrevButton}
      {NextButton}
    </div>
  )
}

export default SliderNavigation
